// HISTORY screen of the redesigned alarms page (`.context/alarms-redesign/
// spec-history.md`): a filter row, day-grouped event cards, a footer row. It
// replaces today's collapsed "Recent activity" disclosure — same read, same
// paging rules, given a screen of its own, because "what has this boat been
// doing?" is a question in its own right and not a footnote to the list.
//
// The screen renders; `lib/alarmHistoryRedesign.ts` decides. Every claim a row
// makes beyond its own landed transition — how long an episode ran, whether
// anyone answered it, who that was — is derived there, under test, because each
// one is a place where the page could quietly say something the wire never did.
//
// What the design asks for that does NOT exist (data-mapping trap 4), and what
// this file renders instead:
//   - Mate rows, mate avatars, the "Mate actions" chip and "undoable" outcomes:
//     history holds STATE transitions only, and there is no undo endpoint. Not
//     rendered at all — a chip that filtered to nothing would be a promise.
//   - Per-episode evidence ("pump running the whole time", "low 11.3 V ·
//     falling"): nothing records a min, max, trend or runtime. The cell carries
//     the one value the boat stamped on the transition, or nothing.
//   - The `Last 30 days ⌄` caret implies a range picker that does not exist yet,
//     so the chip is static and wears no caret: a dead affordance is worse than
//     an honest label.
//
// Paging stays EXPLICIT ("Load older", never infinite scroll), bounded by the
// oldest shown row's time and de-duplicated on the boundary row — and every
// page carries the same 30-day `from`, so the range chip stays true and the
// walk cannot wander out of the window it claims.

import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '../../../api';
import { DownloadIcon } from '../../app/icons';
import type {
  AlarmHistoryEntry,
  AlarmHistoryResponse,
  AlarmSeverity,
  AlarmStateResponse,
} from '../../../lib/alarmDisplay';
import {
  HISTORY_EMPTY,
  HISTORY_EXPORT_FAILED,
  HISTORY_EXPORT_PAGE_SIZE,
  HISTORY_PAGE_ERROR,
  HISTORY_PAGE_SIZE,
  HISTORY_WINDOW_MS,
  buildHistoryRows,
  fetchAllHistory,
  filterHistoryRows,
  groupHistoryByDay,
  historyCountCaption,
  historyCsv,
  historyFilterEmptyText,
  mergeHistoryPage,
  type HistoryFilter,
  type HistoryRow,
} from '../../../lib/alarmHistoryRedesign';
import { useUnits } from '../../../lib/useUnits';

export interface HistoryScreenProps {
  boatId: string;
  /**
   * The alarm-state snapshot — the ONLY source for the per-row title and
   * severity joins. Passed whole (rather than pre-joined) so the screen can
   * also reach `runtime.state` for the "still active" outcome.
   */
  response: AlarmStateResponse;
  /** alertId → title, prebuilt by the page. Missing ⇒ a retired definition. */
  titleById: ReadonlyMap<string, string>;
  /** alertId → severity, for the row's marker dot. Missing ⇒ neutral dot. */
  severityById: ReadonlyMap<string, AlarmSeverity>;
  /**
   * alertId → the STORE unit of the alarm's own signal (ADR 0170). The value a
   * transition recorded is in that same domain, so the two go together; without
   * a unit the evidence cell prints the bare number rather than guessing one.
   */
  unitById: ReadonlyMap<string, string>;
  /**
   * alertId → the alarm's RESOLVED path. Beside `unitById` it decides whether
   * the recorded value may be restated in the account's units (ADR 0189) — the
   * unit alone cannot, since an engine's oil pressure and a barometer both
   * declare hPa and only the barometer converts.
   */
  pathById: ReadonlyMap<string, string>;
  /** The page clock, so "still active · 14 min" ages with everything else. */
  now: number;
}

type FetchState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      entries: AlarmHistoryEntry[];
      hasMore: boolean;
      /**
       * A failed "Load older", said out loud. It must NOT collapse `hasMore`:
       * a network error is a fact about the fetch, not about the window, and
       * flipping the flag would both hide the retry (the button is gated on
       * it) and swap the footer to its everything-is-counted caption.
       */
      pageError: string | null;
    };

const FILTER_CHIPS: { key: HistoryFilter; label: string }[] = [
  { key: 'all', label: 'All events' },
  { key: 'emergency', label: 'Emergencies' },
  { key: 'warning', label: 'Warnings' },
];

/** The 30-day floor, as an ISO instant. Recomputed per call, deliberately. */
function windowStart(): string {
  return new Date(Date.now() - HISTORY_WINDOW_MS).toISOString();
}

export function HistoryScreen({
  boatId,
  response,
  titleById,
  severityById,
  unitById,
  pathById,
  now,
}: HistoryScreenProps) {
  // The account's display units (ADR 0189). Both the rows below and the CSV
  // export go through `buildHistoryRows`, so the file and the screen state a
  // recorded reading in exactly one unit.
  const units = useUnits();
  const [state, setState] = useState<FetchState>({ kind: 'loading' });
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState<HistoryFilter>('all');

  const buildPath = useCallback(
    (to?: string): string => {
      const params = new URLSearchParams();
      params.set('limit', String(HISTORY_PAGE_SIZE));
      params.set('from', windowStart());
      if (to !== undefined) params.set('to', to);
      return `/v1/boats/${boatId}/alerts/history?${params.toString()}`;
    },
    [boatId]
  );

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    void (async () => {
      try {
        const data = await api<AlarmHistoryResponse>(buildPath());
        if (cancelled) return;
        setState({
          kind: 'ready',
          entries: data.entries,
          hasMore: data.entries.length >= HISTORY_PAGE_SIZE,
          pageError: null,
        });
      } catch (err) {
        if (cancelled) return;
        const e = err as { message?: string };
        setState({ kind: 'error', message: e.message ?? 'Failed to load activity.' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buildPath]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (state.kind !== 'ready' || !state.hasMore || loadingMore) return;
    const oldest = state.entries[state.entries.length - 1];
    if (oldest === undefined) return;
    setLoadingMore(true);
    try {
      const data = await api<AlarmHistoryResponse>(buildPath(oldest.time));
      const merged = mergeHistoryPage(state.entries, data.entries, HISTORY_PAGE_SIZE);
      setState({
        kind: 'ready',
        entries: merged.entries,
        hasMore: merged.hasMore,
        pageError: null,
      });
    } catch {
      // A failed "load older" says so and keeps the button as the retry. It
      // must NOT flip `hasMore` off: that would silently promote the loaded
      // slice to "the whole 30 days" in the footer caption.
      setState((s) => (s.kind === 'ready' ? { ...s, pageError: HISTORY_PAGE_ERROR } : s));
    } finally {
      setLoadingMore(false);
    }
  }, [state, loadingMore, buildPath]);

  // Each alarm's CURRENT state, the other half of the "still active" claim: the
  // newest row for an alarm can only speak in the present tense if the snapshot
  // says the alarm is still in a surfaced state right now.
  const stateById = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of response.alerts) {
      if (row.runtime !== null) map.set(row.definition.id, row.runtime.state);
    }
    return map;
  }, [response]);

  const entries = state.kind === 'ready' ? state.entries : undefined;
  // Rows are derived from the WHOLE fetched list before filtering: an episode's
  // outcome is read off its neighbours, and a hidden clear would leave a settled
  // alarm reading as if it were still running.
  const allRows = useMemo(
    () =>
      entries === undefined
        ? []
        : buildHistoryRows(entries, {
            titleById,
            severityById,
            stateById,
            unitById,
            pathById,
            units,
            now,
          }),
    [entries, titleById, severityById, stateById, unitById, pathById, units, now]
  );
  const rows = useMemo(() => filterHistoryRows(allRows, filter), [allRows, filter]);
  const groups = useMemo(() => groupHistoryByDay(rows, now), [rows, now]);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const exportCsv = useCallback(async (): Promise<void> => {
    // The whole 30-DAY WINDOW, walked to exhaustion — not the current chip, and
    // not just the pages the reader happened to load. A filter is a lens on the
    // screen, and the loaded slice is wherever "Load older" stopped; a file
    // someone forwards to a surveyor, an insurer or a yard claims the window on
    // its face and must contain it. `from` is computed ONCE so the window
    // cannot slide between pages, and any failure writes NO file at all — a
    // silently truncated export is the exact lie this walk exists to avoid.
    if (exporting) return;
    setExporting(true);
    setExportError(null);
    const from = windowStart();
    try {
      const entries = await fetchAllHistory(
        async (to) => {
          const params = new URLSearchParams();
          params.set('limit', String(HISTORY_EXPORT_PAGE_SIZE));
          params.set('from', from);
          if (to !== undefined) params.set('to', to);
          const data = await api<AlarmHistoryResponse>(
            `/v1/boats/${boatId}/alerts/history?${params.toString()}`
          );
          return data.entries;
        },
        { pageSize: HISTORY_EXPORT_PAGE_SIZE }
      );
      // Same derived cells as the screen's rows, so nothing in the file claims
      // more than the screen would for the same event.
      const fullRows = buildHistoryRows(entries, {
        titleById,
        severityById,
        stateById,
        unitById,
        pathById,
        units,
        now,
      });
      const blob = new Blob([historyCsv(fullRows)], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `alarm-history-${boatId}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setExportError(HISTORY_EXPORT_FAILED);
    } finally {
      setExporting(false);
    }
  }, [exporting, boatId, titleById, severityById, stateById, unitById, pathById, units, now]);

  return (
    <section className="al-screen" data-testid="alarms-history-screen">
      <div className="al-chips al-hist-toolbar">
        {FILTER_CHIPS.map((chip) => (
          <button
            key={chip.key}
            type="button"
            className={`al-chip${filter === chip.key ? ' al-chip--selected' : ''}`}
            aria-pressed={filter === chip.key}
            onClick={() => setFilter(chip.key)}
            data-testid={`alarms-history-filter-${chip.key}`}
          >
            {chip.label}
          </button>
        ))}
        <span className="al-hist-spacer" />
        {/* A statement of the window every page is fetched with, not a picker. */}
        <span className="al-chip al-hist-range">Last 30 days</span>
        <button
          type="button"
          className="al-chip"
          onClick={() => void exportCsv()}
          disabled={allRows.length === 0 || exporting}
          data-testid="alarms-history-export"
        >
          <DownloadIcon />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {exportError !== null ? (
        <p className="al-note" role="alert" data-testid="alarms-history-export-error">
          {exportError}
        </p>
      ) : null}

      {state.kind === 'loading' ? <p className="al-note">Loading activity…</p> : null}
      {state.kind === 'error' ? <p className="al-note">{state.message}</p> : null}
      {state.kind === 'ready' && state.entries.length === 0 ? (
        <p className="al-note al-note--empty" data-testid="alarms-history-empty">
          {HISTORY_EMPTY}
        </p>
      ) : null}
      {/* Nothing came back and nothing matched are different facts, and an owner
          who filtered to Emergencies deserves to know which one this is. */}
      {state.kind === 'ready' && state.entries.length > 0 && rows.length === 0 ? (
        <p className="al-note" data-testid="alarms-history-filter-empty">
          {historyFilterEmptyText(state.hasMore)}
        </p>
      ) : null}

      {groups.map((group) => (
        <div key={group.key}>
          <h2 className="al-group-heading">{group.heading}</h2>
          <div className="al-card">
            {group.rows.map((row) => (
              <HistoryEventRow key={row.key} row={row} />
            ))}
          </div>
        </div>
      ))}

      {state.kind === 'ready' && state.entries.length > 0 ? (
        <div className="al-links al-hist-footer">
          <span>
            {historyCountCaption({
              loaded: allRows.length,
              matching: rows.length,
              filtered: filter !== 'all',
              hasMore: state.hasMore,
            })}
          </span>
          {state.pageError !== null ? (
            <span
              className="al-hist-page-error"
              role="alert"
              data-testid="alarms-history-page-error"
            >
              {state.pageError}
            </span>
          ) : null}
          {state.hasMore ? (
            <button
              type="button"
              className="al-textbtn"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              data-testid="alarms-history-more"
            >
              {loadingMore ? 'Loading…' : 'Load older'}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * One event, in six cells: time, marker, what happened, what became of it, the
 * reading behind it, and who answered. The last three are frequently empty —
 * that is the point. A blank cell is this page saying it does not know, and it
 * keeps the column alignment that makes a day's worth of rows scannable.
 */
function HistoryEventRow({ row }: { row: HistoryRow }) {
  const outcomeTone =
    row.outcome === null || row.outcome.tone === 'settled' ? '' : ` al-hist-outcome--${row.marker}`;
  return (
    <div className={`al-hist-row${row.tinted ? ' al-hist-row--alarm' : ''}`}>
      <span className="al-hist-time">{row.clock}</span>
      <span className={`al-hist-dot al-hist-dot--${row.marker}`} aria-hidden="true" />
      <span className="al-hist-main">
        <span className={`al-hist-title${row.titleKnown ? '' : ' al-hist-title--retired'}`}>
          {row.title}
        </span>
        <span className="al-hist-detail">{row.detail}</span>
      </span>
      <span className={`al-hist-outcome${outcomeTone}`}>{row.outcome?.text ?? ''}</span>
      <span className="al-hist-evidence">{row.evidence ?? ''}</span>
      <span className="al-hist-ack">{row.acknowledgement ?? ''}</span>
    </div>
  );
}
